// hooks/useSwitches.js
import {
  getSwitchesService,
  resetSwitchByIdService,
  resetAllSwitchesService,
  changeSwitchStatusService,
  getSwitchesInform,
  getSoftwareOptionsService,
  createSwitchService,
  updateSwitchService,
} from "modules/monitoreo/services/redesService";
import { useState, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  categorizeSwitchResponse,
  mapSwitchGroups,
  switchDate,
  switchStats,
  assessSwitchAttempts,
} from "../model/switchModel";

export const useSwitches = () => {
  const [switchGroups, setSwitchGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSwitch, setSelectedSwitch] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Nuevos estados para creación/edición
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSwitch, setEditingSwitch] = useState(null);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [loadingSoftware, setLoadingSoftware] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const categorizeResponse = categorizeSwitchResponse;
  const mapSwitchData = mapSwitchGroups;
  const operationBusy = useRef(false);
  const pendingFetch = useRef(null);
  const readRevision = useRef(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Obtener switches desde la API
  const fetchSwitches = useCallback(async () => {
    const readId = ++readRevision.current;
    setLoading(true);
    setError(null);

    try {
      if (!pendingFetch.current) {
        pendingFetch.current = getSwitchesService();
        const promise = pendingFetch.current;
        promise
          .finally(() => {
            if (pendingFetch.current === promise) pendingFetch.current = null;
          })
          .catch(() => {});
      }
      const response = await pendingFetch.current;
      if (readId !== readRevision.current) return;

      if (!response || !response.response || !Array.isArray(response.response)) {
        throw new Error("Formato de datos inválido: estructura 'response' no encontrada");
      }

      const mappedData = mapSwitchData(response.response);
      setSwitchGroups(mappedData);
      setLastUpdated(new Date().toISOString());

      const totalPorts = mappedData.reduce((sum, group) => sum + group.totalPorts, 0);
      showNotification(
        `Se cargaron ${mappedData.length} switches con ${totalPorts} puertos`,
        "success"
      );
    } catch (err) {
      console.error("Error al obtener switches:", err);

      let errorMessage = "Error al cargar los switches";
      if (err.message.includes("token")) {
        errorMessage = "Sesión expirada. Por favor, inicie sesión nuevamente.";
      } else if (err.message.includes("Formato")) {
        errorMessage = "Error en el formato de datos recibidos";
      } else {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification, mapSwitchData]);

  // Función auxiliar para obtener los datos actualizados
  const getUpdatedSwitchData = useCallback(async () => {
    try {
      ++readRevision.current;
      const response = await getSwitchesService();
      if (!response || !response.response || !Array.isArray(response.response)) {
        throw new Error("Formato de datos inválido");
      }
      setLastUpdated(new Date().toISOString());
      return mapSwitchData(response.response);
    } catch (error) {
      console.error("Error obteniendo datos actualizados:", error);
      return null;
    }
  }, [mapSwitchData]);

  // Función para refrescar los datos
  const refreshSwitchData = useCallback(async () => {
    try {
      const updatedData = await getUpdatedSwitchData();
      if (updatedData) {
        setSwitchGroups(updatedData);
      }
    } catch (error) {
      console.error("Error refrescando datos:", error);
    }
  }, [getUpdatedSwitchData]);

  // Cargar opciones de software
  const loadSoftwareOptions = useCallback(async () => {
    setLoadingSoftware(true);
    try {
      const response = await getSoftwareOptionsService();
      if (response?.response?.software && Array.isArray(response.response.software)) {
        setSoftwareOptions(response.response.software);
      }
    } catch (error) {
      console.error("Error al cargar opciones de software:", error);
      showNotification("Error al cargar opciones de software", "error");
    } finally {
      setLoadingSoftware(false);
    }
  }, [showNotification]);

  // Abrir diálogo de creación
  const openCreateDialog = useCallback(async () => {
    await loadSoftwareOptions();
    setCreateDialogOpen(true);
  }, [loadSoftwareOptions]);

  // Cerrar diálogo de creación
  const closeCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
  }, []);

  // Abrir diálogo de edición
  const openEditDialog = useCallback(
    async (switchGroup) => {
      if (!switchGroup) return;

      await loadSoftwareOptions();
      setEditingSwitch(switchGroup);
      setEditDialogOpen(true);
    },
    [loadSoftwareOptions]
  );

  // Cerrar diálogo de edición
  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditingSwitch(null);
  }, []);

  // Preparar datos para edición
  const prepareEditData = useCallback((switchGroup) => {
    if (!switchGroup || !switchGroup.ports || switchGroup.ports.length === 0) {
      return null;
    }

    // Tomar el primer puerto como referencia (todos tienen los mismos datos)
    const firstPort = switchGroup.ports[0];

    return {
      idSwitch: firstPort.idswitch, // ID del switch para la actualización
      nameSwitch: switchGroup.name,
      ipSwitch: switchGroup.ip,
      software: firstPort.software,
      hardware: switchGroup.hardware,
    };
  }, []);

  // Crear nuevo switch
  const createSwitch = useCallback(
    async (switchData) => {
      try {
        const response = await createSwitchService(switchData);

        // Verificar si la creación fue exitosa
        if (response?.data?.status === 200) {
          showNotification("Switch creado exitosamente", "success");

          // Refrescar datos
          setTimeout(() => {
            refreshSwitchData();
          }, 1000);

          return { success: true, data: response.data };
        } else {
          const errorMsg = response?.data?.message || "Error al crear el switch";
          showNotification(errorMsg, "error");
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Error al crear switch:", error);
        const errorMsg = error.message || "Error al crear el switch";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    },
    [showNotification, refreshSwitchData]
  );

  // Actualizar switch existente
  const updateSwitch = useCallback(
    async (switchData) => {
      try {
        const response = await updateSwitchService(switchData);

        // Verificar si la actualización fue exitosa
        if (response?.data?.status === 200 && response?.data?.data?.success) {
          showNotification(
            response.data.data.message || "Switch actualizado exitosamente",
            "success"
          );

          // Refrescar datos
          setTimeout(() => {
            refreshSwitchData();
          }, 1000);

          return { success: true, data: response.data.data };
        } else {
          const errorMsg = response?.data?.message || "Error al actualizar el switch";
          showNotification(errorMsg, "error");
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Error al actualizar switch:", error);
        const errorMsg = error.message || "Error al actualizar el switch";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    },
    [showNotification, refreshSwitchData]
  );

  // Función: Exportar informe de switches a Excel
  const exportSwitchesReport = useCallback(async () => {
    setExportLoading(true);

    try {
      const response = await getSwitchesInform();

      if (!response || !response.response || !Array.isArray(response.response)) {
        throw new Error("Formato de datos de informe inválido");
      }

      // Preparar datos para Excel
      const excelData = prepareExcelData(response.response);

      // Crear libro de trabajo de Excel
      const wb = XLSX.utils.book_new();

      // Hoja 1: Datos principales
      const wsData = XLSX.utils.json_to_sheet(excelData.mainData);
      XLSX.utils.book_append_sheet(wb, wsData, "Informe Switches");

      // Hoja 2: Resumen
      const wsSummary = XLSX.utils.json_to_sheet(excelData.summary);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

      // Hoja 3: Estadísticas por respuesta
      const wsStats = XLSX.utils.json_to_sheet(excelData.statistics);
      XLSX.utils.book_append_sheet(wb, wsStats, "Estadísticas");

      // Ajustar anchos de columna para la hoja principal
      const wscols = [
        { wch: 25 }, // Nombre
        { wch: 15 }, // IP
        { wch: 10 }, // Puerto
        { wch: 20 }, // Versión
        { wch: 20 }, // Hardware
        { wch: 25 }, // Fecha
        { wch: 40 }, // Respuesta
        { wch: 15 }, // Estado
      ];
      wsData["!cols"] = wscols;

      // Añadir formato condicional para colores de respuesta
      addExcelFormatting(wb, wsData, excelData.mainData);

      // Generar archivo Excel
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      // Descargar el archivo
      downloadExcel(excelBuffer, "informe_switches.xlsx");

      showNotification(
        `Informe exportado exitosamente a Excel (${excelData.mainData.length} registros)`,
        "success"
      );

      return excelData;
    } catch (err) {
      console.error("Error al exportar informe:", err);

      let errorMessage = "Error al exportar el informe";
      if (err.message.includes("token")) {
        errorMessage = "Sesión expirada. Por favor, inicie sesión nuevamente.";
      } else if (err.message.includes("Formato")) {
        errorMessage = "Error en el formato de datos recibidos";
      } else {
        errorMessage = err.message;
      }

      showNotification(errorMessage, "error");
      return null;
    } finally {
      setExportLoading(false);
    }
  }, [showNotification]);

  // Función para preparar datos para Excel
  const prepareExcelData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return {
        mainData: [],
        summary: [],
        statistics: [],
      };
    }

    // Datos principales
    const mainData = apiData.map((item, index) => ({
      ID: index + 1,
      Nombre: item.nombre || "N/A",
      "Dirección IP": item.ip || "N/A",
      Puerto: item.puerto || "N/A",
      "Versión Software": item.version || "N/A",
      Hardware: item.hardware || "N/A",
      Fecha: item.fecha ? formatExcelDate(item.fecha) : "Sin fecha",
      Respuesta: item.respuesta || "Sin respuesta",
      Estado: categorizeResponse(item.respuesta),
      "Último Reset": item.resetdate ? formatExcelDate(item.resetdate) : "Nunca",
    }));

    // Resumen general
    const totalSwitches = [...new Set(apiData.map((item) => item.nombre))].length;
    const totalPuertos = apiData.length;

    const summary = [
      { Métrica: "Total Switches", Valor: totalSwitches },
      { Métrica: "Total Puertos", Valor: totalPuertos },
      { Métrica: "Fecha de Exportación", Valor: new Date().toLocaleString() },
      { Métrica: "Generado por", Valor: "Sistema de Monitoreo" },
    ];

    // Estadísticas por tipo de respuesta
    const responseTypes = {
      success: 0,
      error: 0,
      warning: 0,
      info: 0,
      default: 0,
    };

    apiData.forEach((item) => {
      const type = categorizeResponse(item.respuesta);
      responseTypes[type]++;
    });

    const statistics = Object.entries(responseTypes).map(([type, count]) => ({
      "Tipo Respuesta": type.toUpperCase(),
      Cantidad: count,
      Porcentaje: `${((count / totalPuertos) * 100).toFixed(2)}%`,
    }));

    return {
      mainData,
      summary,
      statistics,
    };
  };

  const formatExcelDate = switchDate;

  // Función para añadir formato condicional a Excel
  const addExcelFormatting = (wb, ws, data) => {
    // Aquí podrías añadir formatos condicionales si necesitas
    // Por ejemplo, colores según el estado

    // Crear un rango para el estado
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Añadir encabezados en negrita
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { horizontal: "center" },
      };
    }
  };

  // Función para descargar Excel
  const downloadExcel = (data, filename) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    link.click();

    window.URL.revokeObjectURL(url);
  };

  // Función para seleccionar un grupo de switches
  const selectSwitchGroup = useCallback((switchGroup) => {
    setSelectedSwitch(switchGroup);
  }, []);

  // Función para seleccionar un puerto específico
  const selectPortForReset = useCallback((port) => {
    setSelectedPort(port);
  }, []);

  // Función para limpiar la selección
  const clearSelection = useCallback(() => {
    setSelectedSwitch(null);
    setSelectedPort(null);
  }, []);

  const changePortStatus = useCallback(
    async (portId, currentStatus) => {
      if (operationBusy.current || portId == null) return false;
      operationBusy.current = true;
      setStatusChanging(true);
      try {
        await changeSwitchStatusService(portId);
        const updated = await getUpdatedSwitchData();
        if (!updated) {
          showNotification(
            "Solicitud enviada; no se pudo confirmar la configuración. Actualiza para verificar.",
            "warning"
          );
          return false;
        }
        setSwitchGroups(updated);
        const port = updated
          .flatMap((group) => group.ports)
          .find((item) => String(item.portId) === String(portId));
        if (!port || port.resetstatus !== !currentStatus) {
          showNotification("No se confirmó el cambio de reinicio automático.", "warning");
          return false;
        }
        showNotification(
          `Reinicio automático ${port.resetstatus ? "habilitado" : "deshabilitado"}`,
          "success"
        );
        return true;
      } catch (err) {
        showNotification(err.message || "No se pudo cambiar la configuración", "error");
        return false;
      } finally {
        operationBusy.current = false;
        setStatusChanging(false);
      }
    },
    [showNotification, getUpdatedSwitchData]
  );

  const resetPort = useCallback(
    async (portId) => {
      if (operationBusy.current || portId == null) return { success: false };
      operationBusy.current = true;
      setResetLoading(true);
      const before = switchGroups,
        started = Date.now();
      try {
        await resetSwitchByIdService(portId);
        const updated = await getUpdatedSwitchData();
        if (!updated) {
          showNotification(
            "Solicitud enviada; no se pudo consultar el resultado del puerto.",
            "warning"
          );
          return { success: false };
        }
        setSwitchGroups(updated);
        const assessment = assessSwitchAttempts(before, updated, [portId], started);
        const port = updated
          .flatMap((group) => group.ports)
          .find((item) => String(item.portId) === String(portId));
        if (assessment.unconfirmed)
          showNotification(
            "Solicitud enviada. El backend aún no registra un resultado nuevo para este puerto.",
            "info"
          );
        else
          showNotification(
            `Puerto ${port?.port}: ${port?.resetresponse}`,
            assessment.success ? "success" : assessment.error ? "error" : "warning"
          );
        return { success: assessment.success === 1, assessment };
      } catch (err) {
        showNotification(err.message || "No se pudo solicitar el reinicio", "error");
        return { success: false };
      } finally {
        operationBusy.current = false;
        setResetLoading(false);
        setSelectedPort(null);
      }
    },
    [switchGroups, showNotification, getUpdatedSwitchData]
  );

  const resetAllSwitches = useCallback(async () => {
    if (operationBusy.current) return { success: false };
    const requested = switchGroups
      .flatMap((group) => group.ports)
      .filter((port) => port.resetstatus)
      .map((port) => port.portId);
    if (!requested.length) {
      showNotification("No hay puertos habilitados para reinicio automático", "warning");
      return { success: false };
    }
    operationBusy.current = true;
    setResetAllLoading(true);
    const started = Date.now(),
      before = switchGroups;
    try {
      await resetAllSwitchesService();
      const updated = await getUpdatedSwitchData();
      if (!updated) {
        showNotification("Solicitud masiva enviada; no se pudo consultar el resultado.", "warning");
        return { success: false };
      }
      setSwitchGroups(updated);
      const assessment = assessSwitchAttempts(before, updated, requested, started);
      showNotification(
        `Resultado de la solicitud: ${assessment.success} reinicios confirmados, ${assessment.error} errores, ${assessment.warning} advertencias y ${assessment.unconfirmed} sin resultado nuevo.`,
        assessment.success === requested.length ? "success" : "warning"
      );
      return { success: assessment.success === requested.length, assessment };
    } catch (err) {
      showNotification(err.message || "No se pudo solicitar el reinicio masivo", "error");
      return { success: false };
    } finally {
      operationBusy.current = false;
      setResetAllLoading(false);
    }
  }, [switchGroups, showNotification, getUpdatedSwitchData]);

  const stats = useMemo(() => switchStats(switchGroups), [switchGroups]);

  return {
    switchGroups,
    loading: loading || resetLoading || resetAllLoading || statusChanging || exportLoading,
    resetLoading,
    resetAllLoading,
    statusChanging,
    exportLoading,
    error,
    selectedSwitch,
    selectedPort,
    notification,
    stats,
    lastUpdated,
    showNotification,
    closeNotification,
    fetchSwitches,
    refreshSwitchData,
    exportSwitchesReport,
    resetPort,
    resetAllSwitches,
    changePortStatus,
    selectSwitchGroup,
    selectPortForReset,
    clearSelection,
    categorizeResponse,

    // Nuevos estados y funciones para creación/edición
    createDialogOpen,
    editDialogOpen,
    editingSwitch,
    softwareOptions,
    loadingSoftware,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
    createSwitch,
    updateSwitch,
    prepareEditData,
  };
};
